import {
  PresignedUrlAction,
  AssetType,
  TransformationVariant,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatButton, KatSpinner } from '@amzn/katal-react';
import { FC, useState, useEffect, useCallback, useRef } from 'react';

import { useClient } from 'src/contexts/ClientContext';
import { validateClient } from 'src/utils/clients/clientUtils';
import { useSmartUpload } from 'src/contexts/SmartUploadContext';
import { useSelection } from 'src/contexts/SelectionContext';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetStatus } from 'src/types';
import { getPresignedUrls } from 'src/utils/clients/controlPlane/apiMethods';
import { generatePreview } from 'src/utils/preview/PreviewGenerator';
import { getPreviewConfig } from 'src/configs/previewConfig';
import { FireTVPreviewState, PreviewConfigBase } from 'src/configs/previewConfigurations/types';

import styles from './SmartUploadPreview.module.css';

const formatVariantName = (variant: TransformationVariant): string =>
  variant
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');

type PreviewTab = 'image' | 'video';

const SmartUploadPreview: FC = () => {
  const {
    batchResults,
    selectedResultIndex,
    setSelectedResultIndex,
    logoTransformResult,
    videoTransformResult,
  } = useSmartUpload();
  const { updateAssetState } = useAssetContext();
  const { controlPlaneClient } = useClient();
  const { placement, deviceFamily } = useSelection();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const fetchingRef = useRef<Set<string>>(new Set());
  const [compositeUrls, setCompositeUrls] = useState<Record<number, string>>({});
  const [previewTab, setPreviewTab] = useState<PreviewTab>('image');

  const result = batchResults[currentIndex];
  const hasVideo =
    videoTransformResult?.status === AssetStatus.COMPLETED &&
    videoTransformResult.transformedAssetKey;

  // Fetch presigned GET URL for a key
  const fetchPreviewUrl = useCallback(
    async (key: string) => {
      if (fetchingRef.current.has(key)) return;
      fetchingRef.current.add(key);
      console.log('[IDEA] Preview: Fetching presigned URL for', key);
      try {
        const client = validateClient(controlPlaneClient, 'Control plane client');
        const urls = await getPresignedUrls(client, PresignedUrlAction.GET, [key]);
        if (urls[0]) {
          setPreviewUrls((prev) => ({ ...prev, [key]: urls[0].presignedUrl }));
        }
      } catch (error) {
        console.error('[IDEA] Preview: Failed to fetch preview URL', error);
        fetchingRef.current.delete(key);
      }
    },
    [controlPlaneClient],
  );

  // Fetch URLs for all completed batch results
  useEffect(() => {
    batchResults.forEach((r) => {
      if (
        r.status === AssetStatus.COMPLETED &&
        r.transformedAssetKey &&
        !fetchingRef.current.has(r.transformedAssetKey)
      ) {
        fetchPreviewUrl(r.transformedAssetKey);
      }
    });
  }, [batchResults, fetchPreviewUrl]);

  // Fetch logo presigned URL when logo transform completes
  useEffect(() => {
    if (
      logoTransformResult?.status === AssetStatus.COMPLETED &&
      logoTransformResult.transformedAssetKey &&
      !fetchingRef.current.has(logoTransformResult.transformedAssetKey)
    ) {
      fetchPreviewUrl(logoTransformResult.transformedAssetKey);
    }
  }, [logoTransformResult, fetchPreviewUrl]);

  // Fetch video presigned URL when video completes
  useEffect(() => {
    if (
      videoTransformResult?.status === AssetStatus.COMPLETED &&
      videoTransformResult.transformedAssetKey &&
      !fetchingRef.current.has(videoTransformResult.transformedAssetKey)
    ) {
      fetchPreviewUrl(videoTransformResult.transformedAssetKey);
    }
  }, [videoTransformResult, fetchPreviewUrl]);

  // Generate composite previews for ALL batch results
  useEffect(() => {
    const logoKey = logoTransformResult?.transformedAssetKey;
    const logoUrl = logoKey ? previewUrls[logoKey] : undefined;
    if (!logoUrl || !placement || !deviceFamily) return;

    let config: PreviewConfigBase;
    try {
      config = getPreviewConfig(placement, deviceFamily);
    } catch {
      return;
    }
    const state = config.defaultState ?? FireTVPreviewState.UNSELECTED;

    batchResults.forEach((r, idx) => {
      const bgUrl = r.transformedAssetKey ? previewUrls[r.transformedAssetKey] : undefined;
      if (!bgUrl || compositeUrls[idx]) return;
      const assets = {
        [AssetType.BACKGROUND_IMAGE]: bgUrl,
        [AssetType.LOGO]: logoUrl,
      };
      generatePreview(assets, state, config)
        .then((url) => setCompositeUrls((prev) => ({ ...prev, [idx]: url })))
        .catch((err) =>
          console.error('[IDEA] Preview: Composite generation failed for index', idx, err),
        );
    });
  }, [batchResults, previewUrls, logoTransformResult, placement, deviceFamily, compositeUrls]);

  if (!batchResults.length) return null;

  const handlePrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const handleNext = () => setCurrentIndex((i) => Math.min(batchResults.length - 1, i + 1));
  const isSelected = selectedResultIndex === currentIndex;

  const handleSelect = () => {
    console.log('[IDEA] Preview: User selected result', {
      index: currentIndex,
      variant: result.variant,
      key: result.transformedAssetKey,
    });
    setSelectedResultIndex(currentIndex);
    if (result.transformedAssetKey) {
      updateAssetState(AssetType.BACKGROUND_IMAGE, {
        status: AssetStatus.COMPLETED,
        key: result.transformedAssetKey,
        transformationVariant: result.variant,
      });
    }
  };

  const imageUrl = result.transformedAssetKey ? previewUrls[result.transformedAssetKey] : undefined;
  const videoUrl = videoTransformResult?.transformedAssetKey
    ? previewUrls[videoTransformResult.transformedAssetKey]
    : undefined;

  // Once a result is selected, show composite/video view instead of carousel
  const showFinalPreview = selectedResultIndex !== null;

  return (
    <div className={styles.container} data-testid="smart-upload-preview">
      {!showFinalPreview && (
        <>
          <div className={styles.navigation}>
            <KatButton
              onClick={handlePrev}
              disabled={currentIndex === 0}
              data-testid="preview-prev-btn"
            >
              &lt; Prev
            </KatButton>
            <span className={styles.counter}>
              {currentIndex + 1} / {batchResults.length}
            </span>
            <KatButton
              onClick={handleNext}
              disabled={currentIndex === batchResults.length - 1}
              data-testid="preview-next-btn"
            >
              Next &gt;
            </KatButton>
          </div>

          <div className={styles.previewCard}>
            {result.status === AssetStatus.TRANSFORMATION_IN_PROGRESS && (
              <div className={styles.loadingContainer}>
                <KatSpinner size="large" />
                <span className={styles.statusText}>Transforming…</span>
              </div>
            )}
            {result.status === AssetStatus.FAILED && (
              <div className={styles.loadingContainer}>
                <span className={`${styles.statusText} ${styles.statusError}`}>
                  {result.error ?? 'Transformation failed'}
                </span>
              </div>
            )}
            {result.status === AssetStatus.COMPLETED && (
              <>
                {imageUrl ? (
                  <img
                    className={styles.previewImage}
                    src={compositeUrls[currentIndex] || imageUrl}
                    alt={`Transformed preview \u2014 ${formatVariantName(result.variant)}`}
                    data-testid="preview-image"
                  />
                ) : (
                  <div className={styles.loadingContainer}>
                    <KatSpinner size="small" />
                    <span className={styles.statusText}>Loading preview…</span>
                  </div>
                )}
              </>
            )}
            <div className={styles.info}>
              <span className={styles.variantName}>
                Template: {formatVariantName(result.variant)}
              </span>
              <span
                className={`${styles.statusText} ${result.status === AssetStatus.COMPLETED ? styles.statusSuccess : result.status === AssetStatus.FAILED ? styles.statusError : ''}`}
              >
                Status:{' '}
                {result.status === AssetStatus.COMPLETED
                  ? 'Completed \u2713'
                  : result.status === AssetStatus.FAILED
                    ? 'Failed \u2717'
                    : 'In Progress\u2026'}
              </span>
            </div>
          </div>

          <div className={styles.actions}>
            {isSelected ? (
              <KatButton variant="primary" disabled data-testid="preview-selected-btn">
                Selected \u2713
              </KatButton>
            ) : (
              <KatButton
                onClick={handleSelect}
                disabled={result.status !== AssetStatus.COMPLETED}
                data-testid="preview-select-btn"
              >
                Select
              </KatButton>
            )}
          </div>
        </>
      )}

      {showFinalPreview && (
        <>
          {hasVideo && (
            <div className={styles.navigation}>
              <KatButton
                variant={previewTab === 'image' ? 'primary' : 'secondary'}
                onClick={() => setPreviewTab('image')}
                data-testid="preview-tab-image"
              >
                Image
              </KatButton>
              <KatButton
                variant={previewTab === 'video' ? 'primary' : 'secondary'}
                onClick={() => setPreviewTab('video')}
                data-testid="preview-tab-video"
              >
                Video
              </KatButton>
            </div>
          )}

          {previewTab === 'image' && (
            <div className={styles.previewCard}>
              <div className={styles.info}>
                <span className={styles.variantName}>
                  {selectedResultIndex !== null && compositeUrls[selectedResultIndex]
                    ? 'Composite Preview'
                    : 'Background Preview'}
                </span>
              </div>
              {selectedResultIndex !== null && compositeUrls[selectedResultIndex] ? (
                <img
                  className={styles.previewImage}
                  src={compositeUrls[selectedResultIndex]}
                  alt="Composite preview with background and logo"
                  data-testid="composite-preview-image"
                />
              ) : imageUrl ? (
                <img
                  className={styles.previewImage}
                  src={imageUrl}
                  alt="Background preview"
                  data-testid="preview-image"
                />
              ) : (
                <div className={styles.loadingContainer}>
                  <KatSpinner size="small" />
                  <span className={styles.statusText}>Loading…</span>
                </div>
              )}
            </div>
          )}

          {previewTab === 'video' && hasVideo && (
            <div className={styles.previewCard}>
              <div className={styles.info}>
                <span className={styles.variantName}>Video Preview</span>
              </div>
              {videoUrl ? (
                <video
                  className={styles.previewImage}
                  src={videoUrl}
                  controls
                  data-testid="preview-video"
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <div className={styles.loadingContainer}>
                  <KatSpinner size="small" />
                  <span className={styles.statusText}>Loading video…</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { SmartUploadPreview };
